import { useEffect, useState } from "react";
import { DoorOpen } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api } from "@/lib/api";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];

function Cell({ c, day, total }) {
  const tone = c.free.length === 0 ? "bg-destructive/10 border-destructive/30" : c.free.length <= Math.ceil(total / 3) ? "bg-amber-500/10 border-amber-500/30" : "bg-primary/5 border-primary/20";
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className={`w-full min-h-[64px] rounded-lg border p-2 text-left transition-colors hover:brightness-95 ${tone}`} data-testid={`free-labs-cell-${c.day}-${c.period}`}>
          <div className="text-[11px] text-muted-foreground mb-1">{c.free.length} of {total} free</div>
          <div className="flex flex-wrap gap-1">
            {c.free.map((n) => <span key={n} className="rounded bg-background border px-1.5 py-0.5 text-[11px] font-medium">{n}</span>)}
            {!c.free.length && <span className="text-xs font-medium text-destructive">All labs in use</span>}
          </div>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 text-sm" data-testid={`free-labs-detail-${c.day}-${c.period}`}>
        <div className="font-semibold mb-2">{day?.label || DAYS[c.day - 1]} P{c.period} · in use</div>
        {c.busy.length ? c.busy.map((b, i) => <div key={i} className="flex justify-between gap-2 py-0.5"><span className="font-medium">{b.lab}</span><span className="text-muted-foreground text-xs text-right">{b.class_name} · {b.teacher_name}</span></div>)
          : <p className="text-muted-foreground text-xs">No lessons timetabled.</p>}
      </PopoverContent>
    </Popover>
  );
}

export function FreeLabsGrid({ week, periods, days = [] }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    if (!week) {
      setData(null);
      setErr(false);
      return undefined;
    }
    let stale = false;
    setErr(false);
    api.get("/timetable/free-labs", { params: { week } }).then((r) => {
      if (!stale) setData(r.data);
    }).catch(() => !stale && setErr(true));
    return () => { stale = true; };
  }, [week]);
  if (!week) {
    return <p className="text-sm text-muted-foreground" data-testid="free-labs-no-school-week">No school days this week.</p>;
  }
  if (err) return <p className="text-sm text-destructive" data-testid="free-labs-error">Couldn't load free labs. Please try again.</p>;
  if (!data) return <p className="text-sm text-muted-foreground" data-testid="free-labs-loading">Loading…</p>;
  const at = (d, p) => data.cells.find((c) => c.day === d && c.period === p);
  return (
    <div data-testid="free-labs-view">
      <p className="text-xs text-muted-foreground mb-3 flex items-center gap-1.5"><DoorOpen className="h-3.5 w-3.5" />Week {week} · based on {data.teachers_with_timetable} uploaded timetable{data.teachers_with_timetable === 1 ? "" : "s"}. Labs used by staff without a timetable won't show as busy. Tap a slot to see who is where.</p>
      <div className="overflow-x-auto rounded-xl border bg-card">
        <table className="w-full min-w-[720px] text-sm table-fixed" data-testid="free-labs-grid">
          <thead><tr className="border-b"><th className="p-3 text-left text-xs text-muted-foreground w-24">Period</th>{DAYS.map((d, index) => <th key={d} className={`p-3 text-left font-semibold ${days[index]?.is_today ? "bg-primary/10" : ""}`} data-testid={`free-labs-day-header-${days[index]?.date || d}`}>{days[index]?.label || d}</th>)}</tr></thead>
          <tbody>{periods.map((p) => (
            <tr key={p.period} className="border-b last:border-0">
              <td className="p-3 align-top"><div className="font-semibold">P{p.period}</div><div className="text-[11px] text-muted-foreground font-mono">{p.start}–{p.end}</div></td>
              {DAYS.map((_, di) => <td key={di} className={`p-1.5 align-top ${days[di]?.is_today ? "bg-primary/5" : ""}`}>{at(di + 1, p.period) && <Cell c={at(di + 1, p.period)} day={days[di]} total={data.labs.length} />}</td>)}
            </tr>
          ))}</tbody>
        </table>
      </div>
    </div>
  );
}
